import { createRoot } from 'react-dom/client';
import { renderToString } from 'react-dom/server';

import MapboxDraw, { DrawSelectionChangeEvent } from '@mapbox/mapbox-gl-draw';
import { toast } from '@raystack/apsara';
import {
  Feature,
  FeatureCollection,
  Geometry,
  Id,
  Polygon,
  Position,
  bbox,
  circle
} from '@turf/turf';
import { throttle } from 'lodash';
import {
  LngLatBounds,
  LngLatLike,
  Map,
  MapMouseEvent,
  Marker,
  MarkerOptions,
  Popup,
  PopupOptions
} from 'mapbox-gl';

import { DEFAULT_MAP_STATE } from '../constants/MAP_LAYER';
import {
  _initialMapConfig,
  _mapBoxDrawStyles,
  circleFromTwoVertexLineString,
  createAddPointOptions,
  createFormattedAreaEl,
  findBottomRightVertex,
  getPolygonArea
} from './common';
import {
  DirectSelect,
  DrawCircle,
  DrawPolygon,
  DrawRectangle,
  SimpleSelect
} from './customModes/index';
import {
  AddGeoJSONLayerProps,
  AddTextLayerProps,
  createBitmapLayer,
  createGeojsonLayer,
  createScatterPlotLayer,
  createTextLayer,
  createTileLayer
} from './deck';

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'mapbox-gl/dist/mapbox-gl.css';

export interface MapProps {
  mapContainer: HTMLElement;
  onAreaChange?: (area: number) => void;
  initialBounds?: [number, number, number, number];
  initialStyle?: string;
  fetchTile?: (
    url: string,
    options?: RequestInit & { signal?: AbortSignal }
  ) => Promise<unknown>;
}

export function map({
  mapContainer,
  onAreaChange,
  fetchTile,
  initialBounds,
  initialStyle
}: MapProps) {
  let _map: Map;
  let draw: MapboxDraw;
  let modes: MapboxDraw.MapboxDrawOptions['modes'];
  const popups: Record<Id, Popup> = {};
  const markers: Record<Id, Marker> = {};

  const _lineStringId = 'line-string';
  const _geojsonOptions = 'geojson-options';
  const _addPoint = 'add-point';

  let _maxArea = 5000; // square km

  const popupContent = document.createElement('div');
  const popupRoot = createRoot(popupContent);

  let customLayers: Parameters<typeof _map.addLayer>[] = [];

  // Centralized queue for style-dependent operations
  let pendingOperations: Array<() => void> = [];
  let isFlushScheduled = false;

  let drawRenderAnimation: null | number = null;
  let hoveredFeatureId: Id | null = null;
  let selectedFeatureId: Id | null = null;
  let measureFeatureId: Id | null = null;
  let drawingFeatureId: Id | null = null;
  let previewFeatureId: Id | null = null;
  let isAreaOverflowing: boolean = false;
  let isMapDrawDisabled: boolean = false;

  let onMeasureFeatureChange: ((geometry: Geometry | null) => void) | null =
    null;

  let currArrMap: Record<Id, number> = {};

  let onFeatureUploadCb: VoidFunction | null = null;
  let onFeatureCreateCb: VoidFunction | null = null;
  let onFeatureDeleteCb: ((feature?: Feature) => void) | null = null;

  /**
   * Queue an operation that requires the map style to be loaded.
   * Operations are batched and executed when the map becomes idle.
   */
  function whenStyleReady(operation: () => void) {
    if (_map.isStyleLoaded()) {
      try {
        operation();
        return;
      } catch {
        // Style became unloaded, fall through to queue
      }
    }

    pendingOperations.push(operation);
    scheduleFlush();
  }

  function scheduleFlush() {
    if (isFlushScheduled) return;
    isFlushScheduled = true;
    _map.once('idle', flushPendingOperations);
  }

  function flushPendingOperations() {
    isFlushScheduled = false;

    if (!_map.isStyleLoaded()) {
      scheduleFlush();
      return;
    }

    const operations = pendingOperations;
    pendingOperations = [];

    for (const operation of operations) {
      try {
        operation();
      } catch {
        // Style became unloaded mid-flush, re-queue remaining operations
        pendingOperations.unshift(
          ...operations.slice(operations.indexOf(operation))
        );
        scheduleFlush();
        return;
      }
    }
  }

  function newPopup(options: PopupOptions) {
    return new Popup({
      closeButton: false,
      closeOnClick: options?.closeOnClick || false,
      ...options
    });
  }

  function getPopup(id: Id, options: PopupOptions) {
    return (
      popups[id] ??
      (popups[id] = newPopup({
        ...options,
        anchor: options?.anchor ?? 'top-right'
      }))
    );
  }

  function removePopup(id: Id) {
    if (popups[id]) {
      popups[id].remove();
      delete popups[id];
    }
  }

  function removeGeojsonPopup() {
    removePopup(_geojsonOptions);
  }

  function newMarker(options?: MarkerOptions) {
    return new Marker(options);
  }

  function getMarker(id: Id, options?: MarkerOptions) {
    return (
      markers[id] ?? (markers[id] = newMarker({ anchor: 'center', ...options }))
    );
  }

  function addMarker(id: Id, coords: LngLatLike, options?: MarkerOptions) {
    return getMarker(id, options).setLngLat(coords).addTo(_map);
  }

  function removeMarker(id: Id) {
    if (markers[id]) {
      markers[id].remove();
      delete markers[id];
    }
  }

  function removeAllMarkers() {
    for (const id in markers) {
      removeMarker(id);
    }
  }

  function fitPolygonBounds(geometry: Polygon) {
    const bounds = (geometry as Polygon).coordinates[0].reduce(
      (bounds, coord) => {
        return bounds.extend(coord as [number, number]);
      },
      new LngLatBounds()
    );

    _map.fitBounds(bounds, {
      padding: DEFAULT_MAP_STATE.padding,
      duration: DEFAULT_MAP_STATE.duration
    });
  }

  function fitBounds(id: string) {
    const feature = draw.get(id);
    if (feature) fitPolygonBounds(feature.geometry as Polygon);
  }

  function fitLayerBounds(
    bounds: [number, number, number, number],
    options?: { animate?: boolean }
  ) {
    const { animate = true } = options ?? {};

    _map.fitBounds(bounds, {
      padding: DEFAULT_MAP_STATE.padding,
      duration: animate ? DEFAULT_MAP_STATE.duration : 0
    });
  }

  function isEmptyFeature(feature: Feature) {
    // Check for empty polygon coordinates
    return !((feature.geometry as Geometry).coordinates as Position[])[0]?.[0];
  }

  function getAllFeatures(isAllowEmptyFeatures: boolean = false) {
    return draw
      .getAll()
      .features.filter(
        d =>
          d.id !== measureFeatureId &&
          d.id !== previewFeatureId &&
          (isAllowEmptyFeatures || !isEmptyFeature(d as Feature))
      ) as Array<Feature>;
  }

  function getFeatureById(featureId: string) {
    return draw.get(featureId);
  }

  function clearAllPopups() {
    for (const id in popups) {
      removePopup(id);
    }
  }

  function addFeatures(geojson: Parameters<typeof draw.add>[0]) {
    return draw.add(geojson);
  }

  function getFeatureId(feature?: Feature) {
    return feature?.id;
  }

  function isValidLineString(feature: Feature) {
    return feature.geometry.type === 'LineString';
  }

  function getModifiedFeature(feature: Feature): {
    modifiedFeature: Feature | null;
    isLineString: boolean;
  } {
    let modifiedFeature = feature as Feature;
    const isLineString = isValidLineString(modifiedFeature);

    if (isLineString) {
      if ((modifiedFeature.geometry as Geometry).coordinates.length <= 1)
        return { modifiedFeature: null, isLineString };

      modifiedFeature = circleFromTwoVertexLineString(feature as Feature);
      modifiedFeature.id = _lineStringId;
    }

    return { modifiedFeature, isLineString };
  }

  function updateMapPaints() {
    whenStyleReady(() => {
      const layers: {
        id: string;
        style: Parameters<typeof _map.setPaintProperty>[1];
      }[] = [
        { id: 'gl-draw-polygon-stroke.cold', style: 'line-color' },
        { id: 'gl-draw-line.cold', style: 'line-color' },
        { id: 'gl-draw-polygon-stroke.hot', style: 'line-color' },
        { id: 'gl-draw-line.hot', style: 'line-color' },
        {
          id: 'gl-draw-polygon-and-line-vertex-halo-active.cold',
          style: 'circle-stroke-color'
        },
        {
          id: 'gl-draw-polygon-and-line-vertex-halo-active.hot',
          style: 'circle-stroke-color'
        },
        {
          id: 'gl-draw-polygon-and-line-vertex-active.hot',
          style: 'circle-color'
        },
        {
          id: 'gl-draw-polygon-and-line-vertex-active.cold',
          style: 'circle-color'
        }
      ];

      const computedStyles = window.getComputedStyle(document.body);
      const rsColorForegoundAccentPrimary = computedStyles.getPropertyValue(
        '--rs-color-foreground-accent-primary'
      );
      const rsColorBorderDangerEmphasis = computedStyles.getPropertyValue(
        '--rs-color-border-danger-emphasis'
      );
      const rsColorForegroundAccentPrimary = computedStyles.getPropertyValue(
        '--rs-color-foreground-accent-primary'
      );

      const borderColor = isAreaOverflowing
        ? rsColorBorderDangerEmphasis
        : rsColorForegoundAccentPrimary;

      for (const layer of layers) {
        if (measureFeatureId || previewFeatureId) {
          _map.setPaintProperty(layer.id, layer.style, [
            'match',
            ['get', 'id'],
            measureFeatureId || previewFeatureId,
            rsColorForegroundAccentPrimary,
            borderColor
          ]);
        } else {
          _map.setPaintProperty(layer.id, layer.style, borderColor);
        }
      }

      // Update fill color for active drawing to match stroke color (handles theme changes)
      _map.setPaintProperty('gl-draw-polygon-fill.hot', 'fill-color', [
        'case',
        ['==', ['get', 'active'], 'true'],
        borderColor,
        'transparent'
      ]);
    });
  }

  function calculateTotalArea() {
    let totalArea = 0;

    for (const id in currArrMap) {
      totalArea += currArrMap[id];
    }

    // Don't do a max area check when -1
    if (_maxArea >= 0) {
      isAreaOverflowing = totalArea > _maxArea;
    }

    onAreaChange?.(totalArea);
    updateMapPaints();
  }

  function initTotalArea() {
    const features = getAllFeatures();

    // Calculation total area
    for (const feature of features) {
      const { modifiedFeature } = getModifiedFeature(feature as Feature);

      if (!modifiedFeature || isEmptyFeature(modifiedFeature)) continue;

      const id = getFeatureId(modifiedFeature);
      if (!id) continue;

      currArrMap[id] = getPolygonArea.utm(modifiedFeature.geometry as Geometry);
    }

    calculateTotalArea();
  }

  function deleteFeature(id: string) {
    removePopup(_geojsonOptions);
    removePopup(id);

    if (id === measureFeatureId) {
      onMeasureReset();
      return;
    }

    if (id === previewFeatureId) {
      removePreviewLayer();
      return;
    }

    const feature = draw.get(id);
    draw.delete(id);

    if (id === selectedFeatureId) {
      selectedFeatureId = null;
    }

    if (id === drawingFeatureId) {
      drawingFeatureId = null;
    }

    draw.changeMode('simple_select');

    delete currArrMap[id];

    calculateTotalArea();
    onFeatureDeleteCb?.(feature);
  }

  function deleteAllFeatures() {
    clearAllPopups();
    draw.deleteAll();
    currArrMap = {};
    calculateTotalArea();
    onFeatureDeleteCb?.();
  }

  function onAddPointDiscard() {
    removeMarker(_addPoint);
    removePopup(_addPoint);
  }

  function uploadGeoJson(
    geojson: Parameters<typeof draw.add>[0],
    options?: { fitBounds?: boolean } & Parameters<typeof fitLayerBounds>[1]
  ) {
    const { fitBounds = true, ...fitLayerBoundsOptions } = options ?? {};

    deleteAllFeatures();

    try {
      addFeatures(geojson);
      initTotalArea();
      onFeatureUploadCb?.();

      if (!fitBounds) return;

      const coordinates = bbox(geojson) as [number, number, number, number];
      fitLayerBounds(coordinates, fitLayerBoundsOptions);
    } catch (err) {
      toast.error('Invalid geojson!', {
        description:
          (err as Error)?.message || 'The geojson provided is not correct.'
      });
    }
  }

  function setMode(mode: 'draw_rectangle' | 'draw_circle' | 'draw_polygon') {
    onAddPointDiscard();

    if (drawingFeatureId) {
      removePopup(drawingFeatureId);
      drawingFeatureId = null;
    }

    draw.changeMode(mode as string);

    const features = getAllFeatures(true);
    const feature = features.at(-1) as Feature | undefined;
    const id = getFeatureId(feature);
    if (id) drawingFeatureId = id;
  }

  function getCenter() {
    return _map.getCenter();
  }

  function getZoom() {
    return _map.getZoom();
  }

  function setCenter(coords: LngLatLike) {
    _map.setCenter(coords);
  }

  function setZoom(zoom: number) {
    _map.setZoom(zoom);
  }

  function goTo(coords: LngLatLike) {
    _map.easeTo({ center: coords, zoom: DEFAULT_MAP_STATE.zoom });
  }

  function zoomTo(...args: Parameters<typeof _map.zoomTo>) {
    _map.zoomTo(...args);
  }

  function setBearing(...args: Parameters<typeof _map.setBearing>) {
    _map.setBearing(...args);
  }

  function getBearing() {
    return _map.getBearing();
  }

  function onZoomChange(cb: VoidFunction) {
    _map.on('zoomend', cb);

    return () => {
      _map.off('zoomend', cb);
    };
  }

  function onBearingChange(cb: VoidFunction) {
    _map.on('rotate', cb);

    return () => {
      _map.off('rotate', cb);
    };
  }

  function onMouseMove(cb: Parameters<typeof _map.on<'mousemove'>>[2]) {
    _map.on('mousemove', cb);

    return () => {
      _map.off('mousemove', cb);
    };
  }

  function onSelectionChange(
    cb: Parameters<typeof _map.on<'draw.selectionchange'>>[2]
  ) {
    _map.on('draw.selectionchange', cb);

    return () => {
      _map.off('draw.selectionchange', cb);
    };
  }

  function onContextMenu(cb: Parameters<typeof _map.on<'contextmenu'>>[2]) {
    _map.on('contextmenu', cb);

    return () => {
      _map.off('contextmenu', cb);
    };
  }

  // get current selected feature id
  // if draw disabled return null
  function getCurrentSelectedFeatureId() {
    if (isMapDrawDisabled) return null;

    const featureId = (drawingFeatureId ||
      selectedFeatureId ||
      measureFeatureId ||
      previewFeatureId) as string | null;

    return featureId;
  }

  function onFeatureUpload(cb: VoidFunction) {
    onFeatureUploadCb = () => {
      cb();
    };

    return () => {
      onFeatureUploadCb = null;
    };
  }

  function onFeatureCreate(cb: VoidFunction) {
    onFeatureCreateCb = () => {
      if (selectedFeatureId) {
        removePopup(selectedFeatureId);
        selectedFeatureId = null;
      }

      if (drawingFeatureId) {
        removePopup(drawingFeatureId);
        drawingFeatureId = null;
      }

      if (previewFeatureId || measureFeatureId) return;

      cb();
    };

    _map.on('draw.create', onFeatureCreateCb);

    return () => {
      _map.off('draw.create', onFeatureCreateCb);
    };
  }

  function onFeatureStartUpdating(cb: (feature: Feature) => void) {
    let isInDirectSelectMode = false;
    let hasTriggeredCallback = false;
    let selectedFeature: string | null = null;
    let isMouseDown = false;

    const modeChangeHandler = (
      e: Parameters<typeof _map.on<'draw.selectionchange'>>[1]
    ) => {
      if (e.mode === 'direct_select') {
        isInDirectSelectMode = true;
        hasTriggeredCallback = false;
        selectedFeature = draw.getSelectedIds();
      } else {
        isInDirectSelectMode = false;
        selectedFeature = null;
        isMouseDown = false;
      }
    };

    const mouseDownHandler = () => {
      if (isInDirectSelectMode && selectedFeature) {
        isMouseDown = true;
      }
    };

    const mouseMoveHandler = () => {
      // When user moves mouse after mousedown in direct_select mode, trigger the callback
      if (
        isMouseDown &&
        isInDirectSelectMode &&
        !hasTriggeredCallback &&
        selectedFeature
      ) {
        hasTriggeredCallback = true;
        cb(draw.get(selectedFeature));
      }
    };

    const mouseUpHandler = () => {
      isMouseDown = false;
    };

    const updateHandler = () => {
      // Reset the flag when update completes so it can trigger again on next edit
      hasTriggeredCallback = false;
    };

    _map.on('draw.modechange', modeChangeHandler);
    _map.on('mousedown', mouseDownHandler);
    _map.on('mousemove', mouseMoveHandler);
    _map.on('mouseup', mouseUpHandler);
    _map.on('draw.update', updateHandler);

    return () => {
      _map.off('draw.modechange', modeChangeHandler);
      _map.off('mousedown', mouseDownHandler);
      _map.off('mousemove', mouseMoveHandler);
      _map.off('mouseup', mouseUpHandler);
      _map.off('draw.update', updateHandler);
    };
  }

  function onFeatureUpdate(cb: Parameters<typeof _map.on<'draw.update'>>[2]) {
    _map.on('draw.update', cb);

    return () => {
      _map.off('draw.update', cb);
    };
  }

  function onFeatureDelete(cb: (feature?: Feature) => void) {
    let isLastPreviewFeature = false;

    onFeatureDeleteCb = feature => {
      if (previewFeatureId) {
        isLastPreviewFeature = true;
        return;
      }

      if (isLastPreviewFeature) {
        isLastPreviewFeature = false;
        return;
      }

      cb(feature);
    };

    return () => {
      onFeatureDeleteCb = null;
    };
  }

  function onMeasureDelete() {
    if (!measureFeatureId) return;

    const feature = draw.getAll().features.at(-1) as Feature | undefined;

    // If user selects measure feature tool but does not draw anything & closes it
    if (
      feature &&
      feature.id === measureFeatureId &&
      draw.getMode() === 'draw_polygon'
    ) {
      draw.changeMode('simple_select');
    }
    const featureIdToDelete = measureFeatureId;

    // Clear state FIRST so deletion works
    onMeasureFeatureChange = null;
    measureFeatureId = null;

    draw.delete(String(featureIdToDelete));
    updateMapPaints();
  }

  function onMeasureReset() {
    if (!measureFeatureId) return;

    const featureIdToDelete = measureFeatureId;

    // Clear state FIRST
    measureFeatureId = null;

    // Delete old feature
    draw.delete(String(featureIdToDelete));

    // Reset callback with null (resets UI)
    onMeasureFeatureChange?.(null);

    // Start new drawing
    draw.changeMode('draw_polygon');

    const id = draw.getAll().features.at(-1)?.id;
    if (!id) return;

    measureFeatureId = id;
    updateMapPaints();
  }

  function onMeasure(cb: (geometry: Geometry | null) => void) {
    draw.changeMode('draw_polygon');

    const id = draw.getAll().features.at(-1)?.id;

    if (id) {
      measureFeatureId = id;
      onMeasureFeatureChange = cb;
      updateMapPaints();
    }
  }

  function addPoint() {
    onAddPointDiscard();
    const marker = getMarker(_addPoint).setDraggable(true);

    function renderAddPointPopup(coords: [number, number]) {
      popupRoot.render(
        createAddPointOptions({
          maxRadius: 39.85,
          minRadius: 1,
          onDiscard: onAddPointDiscard,
          onCreate: function (coords: [number, number], radius: number) {
            onAddPointDiscard();

            const newCircle = circle([...coords].reverse(), radius);

            const ids = addFeatures(newCircle);
            const featureId = ids[0];

            if (!featureId) return;

            const addedFeature = draw.get(featureId);

            if (!addedFeature) return;

            // Fire draw.create event so onFeatureCreate subscribers are notified
            _map.fire('draw.create', {
              features: [addedFeature]
            });

            fitBounds(featureId);
          },
          defaultCoords: [...coords].reverse() as [number, number],
          defaultRadius: 5,
          // @ts-expect-error @ts-ignore
          key: coords.join(',')
        })
      );

      getPopup(_addPoint, {
        anchor: 'top-left',
        className: 'mapboxGlPopupDomContent'
      })
        .setDOMContent(popupContent)
        .setLngLat(coords)
        .addTo(_map);
    }

    function removeAddPointPopup() {
      removePopup(_addPoint);
    }

    // Default marker render on the center of the current map display
    const coordinates = getCenter();
    renderAddPointPopup([coordinates.lng, coordinates.lat]);

    marker.setLngLat([coordinates.lng, coordinates.lat]).addTo(_map);

    marker.on('dragstart', removeAddPointPopup);
    marker.on('dragend', e => {
      const coordinates = e.target.getLngLat();
      renderAddPointPopup([coordinates.lng, coordinates.lat]);
    });
  }

  function updateMapStyle(style: string) {
    // Cancel any pending draw operations to prevent "style not done loading" errors
    if (drawRenderAnimation) {
      cancelAnimationFrame(drawRenderAnimation);
      drawRenderAnimation = null;
    }

    _map.setStyle(style);

    // Re-apply paints and layers after style loads
    updateMapPaints();
    whenStyleReady(flushCustomLayers);
  }

  function getLayer(...args: Parameters<typeof _map.getLayer>) {
    return _map.getLayer(...args);
  }

  function getSource(...args: Parameters<typeof _map.getSource>) {
    return _map.getSource(...args);
  }

  function addSource(...args: Parameters<typeof _map.addSource>) {
    whenStyleReady(() => {
      if (!_map.getSource(args[0])) {
        _map.addSource(...args);
      }
    });
  }

  function addToCustomLayer(...args: Parameters<typeof _map.addLayer>) {
    const id = args[0].id;

    if (!customLayers.some(layer => layer[0].id === id)) {
      customLayers.push(args);
    }
  }

  function flushCustomLayers() {
    for (const args of customLayers) {
      if (!_map.getLayer(args[0].id)) {
        _map.addLayer(...args);
      }
    }
  }

  function addLayer(...args: Parameters<typeof _map.addLayer>) {
    addToCustomLayer(...args);
    whenStyleReady(flushCustomLayers);
  }

  function addScatterPlot(...args: Parameters<typeof createScatterPlotLayer>) {
    return addLayer(createScatterPlotLayer(...args));
  }

  function addTileLayer(options: {
    args: Parameters<typeof createTileLayer>;
    beforeId?: string;
  }) {
    const { args, beforeId = 'gl-draw-polygon-fill.cold' } = options;
    addLayer(createTileLayer(...[...args, fetchTile]), beforeId);
  }

  function addBitmapLayer(options: {
    args: Parameters<typeof createBitmapLayer>;
    beforeId?: string;
  }) {
    const { args, beforeId = 'gl-draw-polygon-fill.cold' } = options;
    addLayer(createBitmapLayer(...[...args, fetchTile]), beforeId);
  }

  function addGeoJSONLayer({ beforeId, ...props }: AddGeoJSONLayerProps) {
    addLayer(
      createGeojsonLayer(props),
      beforeId ?? 'gl-draw-polygon-fill.cold'
    );
  }

  function addTextLayer({ beforeId, ...props }: AddTextLayerProps) {
    addLayer(createTextLayer(props), beforeId);
  }

  function addPreviewLayer(geojson: Feature) {
    const ids = draw.add(geojson as Parameters<typeof draw.add>[0]);
    previewFeatureId = ids.at(-1)!;
    updateMapPaints();
  }

  function removePreviewLayer() {
    if (!previewFeatureId) return;
    draw.delete(previewFeatureId as Parameters<typeof draw.delete>[0]);
    previewFeatureId = null;
    updateMapPaints();
  }

  function removeSource(id: string) {
    whenStyleReady(() => {
      if (_map.getSource(id)) _map.removeSource(id);
    });
  }

  function removeLayer(id: string) {
    customLayers = customLayers.filter(layer => layer[0].id !== id);

    whenStyleReady(() => {
      if (_map.getLayer(id)) _map.removeLayer(id);
    });
  }

  function removeAllLayer() {
    const layersToRemove = [...customLayers];
    customLayers = [];

    whenStyleReady(() => {
      for (const layer of layersToRemove) {
        if (_map.getLayer(layer[0].id)) _map.removeLayer(layer[0].id);
      }
    });
  }

  function setPopupHtml(area: number) {
    return renderToString(createFormattedAreaEl(area));
  }

  function renderFeatureAreaPopup(
    feature: Feature | undefined,
    area: number,
    popupId: Id
  ) {
    if (!feature || !area) return;

    const bottomRightCoord = findBottomRightVertex(feature);

    if (!bottomRightCoord) return 0;

    const popup = getPopup(popupId, {
      anchor: 'top-right',
      className: 'mapboxGlPopupCustom'
    });
    popup.setLngLat(bottomRightCoord).setHTML(setPopupHtml(area)).addTo(_map);
  }

  function setMaxArea(area: number) {
    _maxArea = area;
  }

  function drawRender() {
    if (!_map.isStyleLoaded()) return;

    removePopup(_lineStringId);
    delete currArrMap[_lineStringId];

    const id =
      previewFeatureId ||
      measureFeatureId ||
      selectedFeatureId ||
      drawingFeatureId;

    if (!id || id === previewFeatureId) return;

    const feature = draw.get(id as string);
    if (!feature) return;
    const { modifiedFeature } = getModifiedFeature(feature as Feature);

    if (!modifiedFeature || isEmptyFeature(modifiedFeature)) return;

    const featureId = getFeatureId(modifiedFeature);

    if (!featureId) return;

    if (featureId === measureFeatureId) {
      onMeasureFeatureChange?.(modifiedFeature.geometry as Geometry);
      return;
    }

    const currArea = getPolygonArea.utm(modifiedFeature.geometry as Geometry);

    currArrMap[featureId] = currArea;

    renderFeatureAreaPopup(modifiedFeature, currArea, featureId);
    calculateTotalArea();
  }

  function downloadGeoJSON(
    geoJsonObject: FeatureCollection | Feature | undefined,
    fileName = 'data'
  ) {
    if (!geoJsonObject) return;

    const object = { ...geoJsonObject };

    const blob = new Blob([JSON.stringify(object, null, 2)], {
      type: 'application/json'
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.geojson`;
    document.body.appendChild(a); // Append to body for Firefox
    a.click();
    document.body.removeChild(a); // Clean up
    URL.revokeObjectURL(url); // Free memory
  }

  function drawContextMenu(
    e: MapMouseEvent,
    children: (featureId: string) => React.ReactNode
  ) {
    if (isMapDrawDisabled) return;

    const features = _map.queryRenderedFeatures(e.point, {
      layers: [
        'gl-draw-polygon-and-line-vertex-halo-active.hot',
        'gl-draw-line.hot',
        'gl-draw-polygon-stroke.hot',
        'gl-draw-polygon-fill.hot',
        'gl-draw-polygon-and-line-vertex-halo-active.cold',
        'gl-draw-polygon-and-line-vertex-active.hot',
        'gl-draw-polygon-and-line-vertex-active.cold',
        'gl-draw-line.cold',
        'gl-draw-polygon-stroke.cold',
        'gl-draw-polygon-fill.cold'
      ]
    });

    if (features.length === 0) return;

    const propertyId = features[0].properties?.id;

    if (!propertyId) return;

    const feature = draw.get(propertyId);

    if (!feature) return;

    const id = feature.id;

    if (!id || id === measureFeatureId || id === previewFeatureId) return;

    popupRoot.render(children(String(id)));

    getPopup(_geojsonOptions, {
      anchor: 'top-left',
      closeOnClick: true,
      className: 'mapboxGlPopupDomContent'
    })
      .setDOMContent(popupContent)
      .setLngLat(e.lngLat)
      .addTo(_map);
  }

  function addPolygonContextMenu(
    children: Parameters<typeof drawContextMenu>['1']
  ) {
    return onContextMenu(e => {
      requestAnimationFrame(() => drawContextMenu(e, children));
    });
  }

  function disableMapDraw() {
    //TODO: Triggers an un-neccessary UI flicker, which should be avoided
    isMapDrawDisabled = true;

    function disableSelect() {
      draw.changeMode('simple_select');
    }

    return onSelectionChange(disableSelect);
  }

  function init(accessToken: string) {
    return new Promise(resolve => {
      _map = new Map(
        _initialMapConfig(
          mapContainer,
          accessToken,
          true,
          initialBounds,
          initialStyle
        )
      );

      let isStyleLoaded = false;
      let isMapLoaded = false;

      function onMapLoad() {
        if (!isStyleLoaded || !isMapLoaded) return;

        modes = {
          ...MapboxDraw.modes,
          draw_rectangle: DrawRectangle,
          draw_circle: DrawCircle,
          draw_polygon: DrawPolygon,
          direct_select: DirectSelect,
          simple_select: SimpleSelect
        } as const;

        const computedStyles = window.getComputedStyle(document.body);

        const rsColorForegoundAccentPrimary = computedStyles.getPropertyValue(
          '--rs-color-foreground-accent-primary'
        );
        const rsColorBackgroundBasePrimary = computedStyles.getPropertyValue(
          '--rs-color-background-base-primary'
        );

        draw = new MapboxDraw({
          displayControlsDefault: false,
          modes,
          styles: _mapBoxDrawStyles({
            rsColorForegoundAccentPrimary,
            rsColorBackgroundBasePrimary
          })
        });

        _map.addControl(draw);

        const throlledMouseMove = throttle((e: MapMouseEvent) => {
          const features = _map.queryRenderedFeatures(e.point, {
            layers: ['gl-draw-polygon-fill.hot', 'gl-draw-polygon-fill.cold']
          });

          if (features.length > 0) {
            const propertyId = features[0].properties?.id;

            if (propertyId) {
              const feature = draw.get(propertyId);
              const id = feature?.id;

              if (
                !id ||
                id === measureFeatureId ||
                id === previewFeatureId ||
                id === selectedFeatureId ||
                id === hoveredFeatureId ||
                id === drawingFeatureId
              ) {
                return;
              }

              if (hoveredFeatureId) removePopup(hoveredFeatureId);

              hoveredFeatureId = id;

              renderFeatureAreaPopup(feature as Feature, currArrMap[id], id);
            }
          } else if (hoveredFeatureId) {
            removePopup(hoveredFeatureId);
            hoveredFeatureId = null;
          }
        }, 300);

        _map.once('idle', () => {
          _map.resize();
          flushCustomLayers();
        });

        _map.on('draw.render', () => {
          if (drawRenderAnimation) cancelAnimationFrame(drawRenderAnimation);
          drawRenderAnimation = requestAnimationFrame(drawRender);
        });

        onMouseMove(
          throlledMouseMove as unknown as Parameters<typeof onMouseMove>[0]
        );
        onSelectionChange((e: DrawSelectionChangeEvent) => {
          if (e.features.length > 0) {
            const feature = e.features[0];
            const { id } = feature;

            if (
              !id ||
              id === measureFeatureId ||
              id === previewFeatureId ||
              id === selectedFeatureId ||
              id === drawingFeatureId
            ) {
              return;
            }

            if (selectedFeatureId) removePopup(selectedFeatureId);
            if (drawingFeatureId) removePopup(drawingFeatureId);
            if (hoveredFeatureId) removePopup(hoveredFeatureId);

            selectedFeatureId = id;
            hoveredFeatureId = null;
          } else if (selectedFeatureId) {
            removePopup(selectedFeatureId);
            selectedFeatureId = null;
          }
        });
        resolve(_map);
      }

      _map.once('style.load', () => {
        isStyleLoaded = true;
        onMapLoad();
      });

      _map.once('load', () => {
        isMapLoaded = true;
        onMapLoad();
      });
    });
  }

  function addCustomPopup(
    id: string,
    createElement: (onDiscard: () => void) => React.ReactNode,
    coordinates: LngLatLike
  ) {
    function onDiscard() {
      popups[id].remove();
    }

    popupRoot.render(createElement(onDiscard));

    getPopup(id, { anchor: 'top-left' })
      .setDOMContent(popupContent)
      .setLngLat(coordinates)
      .addTo(_map);
  }

  function removeCustomPopup(id: string) {
    if (popups[id]) popups[id].remove();
  }

  function dispose() {
    clearAllPopups();
    popupContent.remove();

    // If dispose is called before _map initialize
    if (_map) {
      _map.removeControl(draw);
      _map.remove();
    }
  }

  function getCurrentMap() {
    return _map;
  }

  function setFeatureSelectable(id: string, selectable: boolean) {
    const feature = draw.get(id);
    if (!feature) return;
    feature.properties = { ...feature.properties, selectable };
    draw.add(feature); // re-add with updated props
  }

  return {
    init,
    dispose,
    updateMapStyle,
    setMode,
    uploadGeoJson,
    fitBounds,
    fitLayerBounds,
    addScatterPlot,
    addTileLayer,
    addBitmapLayer,
    addGeoJSONLayer,
    addTextLayer,
    addSource,
    addLayer,
    getLayer,
    getSource,
    removeLayer,
    removeAllLayer,
    removeSource,
    addPreviewLayer,
    removePreviewLayer,
    setCenter,
    setZoom,
    setBearing,
    addMarker,
    removeMarker,
    removeAllMarkers,
    addPoint,
    getCenter,
    goTo,
    zoomTo,
    getZoom,
    getBearing,
    onZoomChange,
    onBearingChange,
    onMouseMove,
    onMeasure,
    onMeasureDelete,
    onMeasureReset,
    onFeatureUpload,
    onFeatureCreate,
    onFeatureStartUpdating,
    onFeatureUpdate,
    onFeatureDelete,
    setMaxArea,
    getAllFeatures,
    getFeatureById,
    getCurrentSelectedFeatureId,
    deleteAllFeatures,
    deleteFeature,
    downloadGeoJSON,
    disableMapDraw,
    addCustomPopup,
    removeCustomPopup,
    addPolygonContextMenu,
    removeGeojsonPopup,
    getCurrentMap,
    setFeatureSelectable
  };
}

export type MapInterface = ReturnType<typeof map>;
